import { Skeleton, Stack } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { FiMinus } from "react-icons/fi";
import { setMetaTags } from "../../utils/seo.utils";
import { NavLink, useNavigate } from "react-router-dom";

interface IMobileFooterLinks {
    title: string;
    active?: boolean;
    action: string | (() => void);
}

interface NeoPageLogo {
    white?: string;
    black?: string;
}

interface NeoPageLayoutProps {
    pageTitle: string;
    pageDescription: string;
    isLoading?: boolean;
    logoUrl?: NeoPageLogo | string;
    logoStyle?: React.CSSProperties;
    logoAction: string | (() => void);
    links: IMobileFooterLinks[];
    mobileFooterLinks?: IMobileFooterLinks[];
    copyRightText?: string;
    children: any;
}

export default function NeoPageLayout(props: NeoPageLayoutProps) {
    const navigate = useNavigate();

    const loadingSkeletonRef = useRef(null);
    // Small nav toggle
    const [showSmSideNav, setShowSmSideNav] = useState(false);

    function toggleSideNav(state: boolean) {
        setShowSmSideNav(state);
    }

    // when props change
    useEffect(() => {
        const loadingSkeleton = loadingSkeletonRef.current as unknown as HTMLElement;
        if (!props.isLoading) {
            loadingSkeleton.style.display = "none";
        } else {
            loadingSkeleton.style.display = "block";
        }
    }, [props.isLoading]);

    useEffect(() => {
        setMetaTags({ title: props.pageTitle, description: props.pageDescription, disableTrailAppName: true });
        document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
            anchor.addEventListener("click", function (e) {
                e.preventDefault();
                // @ts-ignore
                document.querySelector(e.target.getAttribute("href"))?.scrollIntoView({
                    behavior: "smooth",
                });
            });
        });
    }, []);

    const runLogoAction = () => {
        if (typeof props.logoAction === "string") {
            navigate(props.logoAction);
        } else if (typeof props.logoAction === "function") {
            props.logoAction();
        }
    };

    return (
        <main id="landing-page-view">
            <nav id="navbar" className="landing-nav">
                <ul className="landing-nav-links">
                    <li>
                        <button onClick={runLogoAction}>
                            {
                                props.logoUrl && typeof props.logoUrl === "string" && (
                                    <img style={props.logoStyle || {}} className="logo white" src={props.logoUrl} alt="" />
                                )
                            }
                            {
                                props.logoUrl && typeof props.logoUrl === "object" && (
                                    <>
                                        <img style={props.logoStyle || {}} className="logo white" src={props.logoUrl?.white || "/NeoWorlder_Logo_white.png"} alt="" />
                                        <img style={props.logoStyle || {}} className="logo black" src={props.logoUrl?.black || "/NeoWorlder_Logo_black.png"} alt="" />
                                    </>
                                )
                            }
    
                        </button>
                    </li>
                </ul>
                <ul className="landing-nav-links sm right">
                    <li>
                        {!showSmSideNav && (
                            <button onClick={() => toggleSideNav(true)} className="sm-toggle-sidenav">
                                <FiMinus className="landing-nav-links-icon" />
                            </button>
                        )}
                    </li>
                </ul>
                <ul className="landing-nav-links lg right">
                    {props.links.map((link, index) => {
                        if (typeof link.action === "function") {
                            return (
                                <li key={"link-" + index}>
                                    <button
                                        className={`landing-nav-link ${link.active ? "active" : ""}`}
                                        onClick={() => {
                                            toggleSideNav(false);
                                            setTimeout(() => {
                                                (link as any).action();
                                            }, 400);
                                        }}
                                    >
                                        {link.title}
                                    </button>
                                </li>
                            );
                        } else {
                            return (
                                <li key={"link-" + index}>
                                    <NavLink to={link.action} className={({ isActive }) => (isActive ? "active landing-nav-link" : "landing-nav-link")}>
                                        <button>{link.title}</button>
                                    </NavLink>
                                </li>
                            );
                        }
                    })}
                </ul>
            </nav>

            <div className={`landing-page-wrapper ${showSmSideNav ? "side-open" : ""}`}>
                <div ref={loadingSkeletonRef} className="skeleton-loader overview-panel">
                    <div className="relative h-full">
                        <Skeleton className="w-full h-full" />
                    </div>
                    <div className="h-full">
                        <div className="text-container-skeleton">
                            <Stack className="items-center">
                                <Skeleton width="420px" height="20px" />
                                <Skeleton width="420px" height="25px" />
                                <Skeleton width="420px" height="50px" />
                                <Skeleton width="420px" height="45px" />
                            </Stack>
                        </div>
                    </div>
                </div>

                {props.children}
            </div>

            <div className={`mobile-nav-links-container ${showSmSideNav ? "side-open" : ""}`}>
                <button onClick={() => toggleSideNav(false)} className="close-btn"></button>
                <ul className="main-links">
                    {props.links.map((link, index) => {
                        if (typeof link.action === "function") {
                            return (
                                <li key={"mobile-link-" + index}>
                                    <button
                                        onClick={() => {
                                            toggleSideNav(false);
                                            setTimeout(() => {
                                                (link as any).action();
                                            }, 400);
                                        }}
                                    >
                                        {link.title}
                                    </button>
                                </li>
                            );
                        } else {
                            return (
                                <li key={"mobile-link-" + index}>
                                    <NavLink to={link.action}>
                                        <button>{link.title}</button>
                                    </NavLink>
                                </li>
                            );
                        }
                    })}
                </ul>

                <ul className="bottom-links">
                    {props.mobileFooterLinks?.map((link, index) => {
                        if (typeof link.action === "function") {
                            return (
                                <li key={"footer-link-" + index}>
                                    <button
                                        onClick={() => {
                                            toggleSideNav(false);
                                            setTimeout(() => {
                                                (link as any).action();
                                            }, 400);
                                        }}
                                    >
                                        {link.title}
                                    </button>
                                </li>
                            );
                        } else {
                            return (
                                <li key={"footer-link-" + index}>
                                    <a target="_blank" href={link.action}>
                                        {link.title}
                                    </a>
                                </li>
                            );
                        }
                    })}
                </ul>
                <div className="mobile-nav-links-footer">
                    {/*<p className="">v0.1.1</p>*/}
                    <p className="">&copy; {props.copyRightText || "2024 ATS Global, Inc."}</p>
                </div>
            </div>
        </main>
    );
}
